<!--htmlhint inline-style-disabled:false -->

<svelte:window on:keyup={keyUpHandler} on:resize={windowResize} />

{#if enabled}
    <div class="prom-modal2__wrapper">
        <div class="prom-modal2__backdrop" on:click={() => clickOutsideCloses && cancel()}></div>
        <section
            {id}
            bind:this={dialogElement}
            role="dialog"
            class="prom-modal2__dialog"
            class:prom-modal2__dialog--wide={wide}
            style={modalStyle}
            aria-labelledby="{id}-header"
            aria-describedby={describedById}
            aria-modal="true">

            <h1 id="{id}-header" class="prom-modal2__header">
                <slot name="title" />
                {#if titleText}{titleText}{/if}
            </h1>

            <div class="prom-modal2__inner">
                <div
                    class="prom-modal2__content"
                    bind:this={contentElement}
                    on:scroll={contentScroll}>

                    {#if $$slots.banner}
                        <div class="prom-modal2__banner-slot">
                            <slot name="banner" />
                        </div>
                    {/if}

                    {#if !titleOnly}
                        <div id="{id}-body" class="prom-modal2__body" class:prom-modal2__body--scroll={scrollBody}>
                            <slot name="body" />
                            {#if bodyText}{bodyText}{/if}
                            {#if bodyHtml}{@html bodyHtml}{/if}
                        </div>
                    {/if}
                </div>

                <div class="prom-modal2__content-gradient">
                    <div
                        class="prom-modal2__content-gradient--inner"
                        style={gradientStyle}
                    ></div>
                </div>

                <div class="prom-modal2__footer">
                    <slot name="footer">
                        <Button
                            type={confirmButtonType}
                            htmlType="button"
                            on:click={confirm}
                            text={confirmText}
                            large
                        />
                        {#if showCancel}
                        <Button
                            type={cancelButtonType}
                            htmlType="button"
                            on:click={cancel}
                            text={cancelText}
                            large
                        />
                        {/if}
                    </slot>
                </div>

            </div>
        </section>
    </div>
{/if}

<script>
    import TabTrapHelper from '../../../../lib/helpers/TabTrapHelper';
    import { normalizeKey } from '@itslearning/atlas/keyboard/normalize';
    import { Keys } from '@itslearning/atlas/keyboard/keys';
    import Button from '../../../inputs/Button/v1/Button.svelte';
    import { createEventDispatcher } from 'svelte';

    const dispatch = createEventDispatcher();

    /** @type {string} Defaults to a random string */
    export let id = Math.random().toString(36).substr(2, 16);
    /** @type {boolean} */
    export let enabled = false;
    /** @type {boolean} */
    export let wide = false;
    /** @type {boolean} */
    export let titleOnly = false;
    /** @type {string} */
    export let confirmText = undefined;
    /** @type {string} */
    export let cancelText = undefined;
    /** @type {string} */
    export let titleText = undefined;
    /** @type {string} */
    export let bodyText = undefined;
    /** @type {string} */
    export let bodyHtml = undefined;
    /** @type {boolean} If the cancel button should be included, remember even without it
     * the modal can still be canceled with escape or clicking the backdrop.*/
    export let showCancel = true;
    /** @type {boolean} If the body slot should scroll on its own without the title.*/
    export let scrollBody = false;
    /** @type {string} */
    export let describedBy = undefined;

    /** @type {string|'basic'|'basic-negative'|'primary'|'secondary'|'destructive'|'plain'} */
    export let confirmButtonType = 'primary';
    /** @type {string|'basic'|'basic-negative'|'primary'|'secondary'|'destructive'|'plain'} */
    export let cancelButtonType = 'secondary';

    /** @type {boolean} */
    export let clickOutsideCloses = true;

    let hasScroll = false;
    let contentScrollRemaining = 50;
    let scrollContainer = document.querySelector('html');
    let promptResolve = undefined;
    let modalStyle = '';

    let focusedBeforeModal = undefined;
    let tabTrapper = undefined;

    let dialogElement;
    let contentElement;

    let gradientStyle;

    $: {
        let height = 0;

        if (hasScroll) {
            height = Math.min(contentScrollRemaining, 30);
        }

        gradientStyle = `height: ${height}px;`;
    }

    $: describedById =describedBy || `${id}-body`;

    let previouslyEnabled = false;

    $: if (enabled && !previouslyEnabled) {
        previouslyEnabled = enabled;
        onOpen();
    } else if (!enabled && previouslyEnabled) {
        previouslyEnabled = enabled;
        onClose();
    }

    $: if (enabled && wide) {
        windowResize();
    }

    function onOpen() {
        focusedBeforeModal = document.activeElement;

        scrollContainer.classList.add('prom-modal2--open-within');

        // Run window resize in case window sizes have changed while
        // the modal was disabled
        windowResize();

        // Set timeout to let the dialog enter the DOM
        setTimeout(() => {
            if (dialogElement) {
                const buttons = dialogElement.querySelectorAll('button');
                tabTrapper = new TabTrapHelper(dialogElement);

                tabTrapper.init();

                // Focus the last button in the dialog, either the cancel button
                // or confirm if there is only one
                if (buttons.length) {
                    buttons[buttons.length - 1].focus();
                }
            }
        }, 0);

        // Run windowResize again after animations have completed
        setTimeout(() => windowResize(), 300);
    }

    export function reinitTabTrapper() {
        if (tabTrapper) {
            tabTrapper.init();
        }
    }

    function onClose() {
        scrollContainer.classList.remove('prom-modal2--open-within');

        // Return focus to the element that was focused before opening
        if (focusedBeforeModal) {
            focusedBeforeModal.focus();
        }

        // Disable the tab trap
        if (tabTrapper) {
            tabTrapper.teardown();
        }
    }

    function keyUpHandler(event) {
        if (enabled && (normalizeKey(event.key) === Keys.ESCAPE)) {
            cancel(event);
        }
    }

    function windowResize() {
        if (!enabled) {
            return;
        }

        // size-700 from top and bottom
        const verticalGap = 24 * 2;

        // Max modal height is 600 and the page height
        // Do not apply the 600 height limit on small screens, let
        // the modal fill the screen in all dimensions
        let maxHeight = window.innerWidth > 640
            ? Math.min(window.innerHeight - verticalGap, wide ? 1200 : 600)
            : window.innerHeight - verticalGap;

        modalStyle = `max-height: ${maxHeight}px;`;

        setTimeout(() => {
            if (contentElement) {
                hasScroll = contentElement.clientHeight !== contentElement.scrollHeight;
            }
        }, 0);
    }

    function cancel() {
        enabled = false;

        dispatch('close', { confirmed: false });
        dispatch('cancel');

        if (promptResolve) {
            promptResolve(false);
        }
    }

    function confirm() {
        enabled = false;

        dispatch('close', { confirmed: true });
        dispatch('confirm');

        if (promptResolve) {
            promptResolve(true);
        }
    }

    export function prompt(properties) {
        if (properties.titleOnly !== undefined) titleOnly = properties.titleOnly;
        if (properties.confirmText !== undefined) confirmText = properties.confirmText;
        if (properties.cancelText !== undefined) cancelText = properties.cancelText;
        if (properties.titleText !== undefined) titleText = properties.titleText;
        if (properties.bodyText !== undefined) bodyText = properties.bodyText;
        if (properties.bodyHtml !== undefined) bodyHtml = properties.bodyHtml;
        if (properties.titleOnly !== undefined) titleOnly = properties.titleOnly;
        if (properties.showCancel !== undefined) showCancel = properties.showCancel;
        if (properties.describedBy !== undefined) describedBy = properties.describedBy;
        if (properties.confirmButtonType !== undefined) confirmButtonType = properties.confirmButtonType;
        if (properties.cancelButtonType !== undefined) cancelButtonType = properties.cancelButtonType;

        enabled = true;

        return new Promise(resolve => {
            promptResolve = resolve;
        });
    }

    function contentScroll() {
        const scrollHeight = contentElement.scrollHeight;
        const clientHeight = contentElement.clientHeight;
        const scrollTop = contentElement.scrollTop;

        contentScrollRemaining = scrollHeight - (clientHeight + scrollTop);
    }
</script>
